import React, { FC, memo } from 'react';
import { WarehouseForBooking } from 'entities/Warehouse';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useGeolocation } from 'shared/utils/hooks/useGeolocation';
import { calculateDistance } from 'widgets/SelectWarehouseMap/utils/helpers/calculateDistance';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { ReadMore } from 'shared/ui/ReadMore';
import { ReactComponent as LocationIcon } from 'shared/assets/icons/newIcons/LocationIcon.svg';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';

interface ModernWarehouseInfoProps {
  warehouseInfo: WarehouseForBooking;
}

export const ModernWarehouseInfo: FC<ModernWarehouseInfoProps> = memo((props) => {
  const { warehouseInfo } = props;
  const { t } = useAppTranslation('booking');
  const { isDesktop } = useScreenBreakpoints();
  const userCoords = useGeolocation();

  const fullAddress = `${warehouseInfo?.address || ''} ${warehouseInfo?.zip || ''}`;

  const distance = userCoords
    ? roundNumber(calculateDistance(userCoords.lat, userCoords.lng, warehouseInfo.latitude, warehouseInfo.longitude))
    : null;

  const warehouseFeatures = [{ label: t('Cameras monitoring') }, { label: t('24 / 7 access') }, { label: t('Online payments') }];

  // Add office feature if warehouse has offices
  if (warehouseInfo?.hasOffices) {
    warehouseFeatures.push({
      label: t('Office rent'),
    });
  }

  return (
    <div className="p-5 h-full flex flex-col">
      <div className="mb-2">
        <h2 className="text-36 font-bold text-primary mb-2 font-tektur">{warehouseInfo?.name}</h2>
        <div className="flex items-center text-grey">
          <LocationIcon className="w-5 h-5 mr-2 -ml-1 [&>path]:stroke-grey" />
          <span className="text-md text-grey">{fullAddress}</span>
          {distance && (
            <>
              <span className="mx-4">•</span>
              <span className="text-md font-medium text-grey">{distance} км от вас</span>
            </>
          )}
        </div>
      </div>

      {warehouseInfo?.description && (
        <div className="mb-4">
          <div className="text-lg font-semibold text-primary mb-2">{t('Description')}</div>
          <ReadMore className="text-grey" html={getLocalizedString(warehouseInfo.description)} rows={2} />
        </div>
      )}

      {isDesktop && (
        <div id="warehouse-features-section" className="mt-auto">
          <div className="flex flex-wrap gap-2">
            {warehouseFeatures.map((feature, index) => (
              <div key={index} className="px-3 py-1 text-primaryLight text-m rounded-full border border-primaryLight">
                {feature.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});
